import { IAdminRole, IPermission, IRole, IRolePermission } from 'types/income';
import { converters as tableConverters } from 'store/table';
import { loaders } from './permissions.loaders';
import {
  AdminRoleDetails,
  Permission,
  PermissionCategoryGroup,
  permissionCategories,
  Role,
  RolePermission,
} from './permissions.types';

const convertPermission = (permission: IPermission): Permission => {
  return {
    permission: permission.permission,
    category: permission.category,
  };
};

const convertPermissions = (permissions: IPermission[]): Permission[] => {
  return permissions.map(convertPermission);
};

const convertRolePermission = (
  rolePermission: IRolePermission,
): RolePermission => {
  return {
    ...convertPermission(rolePermission),
    accessLevel: rolePermission.accessLevel,
  };
};

const convertRolePermissions = (
  rolePermissions: IRolePermission[],
): RolePermission[] => {
  return rolePermissions.map(convertRolePermission);
};

const groupByCategory = <T extends Permission>(
  permissions: T[],
): PermissionCategoryGroup<T>[] => {
  const grouped = Object.groupBy(permissions, ({ category }) => category);

  return permissionCategories.reduce<PermissionCategoryGroup<T>[]>(
    (acc, category) => {
      const categoryPermissions = grouped[category];

      if (!categoryPermissions?.length) {
        return acc;
      }

      acc.push({
        category,
        permissions: categoryPermissions,
      });

      return acc;
    },
    [],
  );
};

export const convertAdminRole = (role: IAdminRole): AdminRoleDetails => {
  const permissions = convertRolePermissions(role.permissions);
  const isSystem = role.roleType !== 'custom';

  return {
    id: isSystem ? role.roleType : role.id,
    roleType: role.roleType,
    isSystem,
    name: role.name,
    description: role.description ?? '',
    permissions,
    categories: groupByCategory(permissions),
  };
};

const convertRole = (role: IRole): Role => {
  return {
    ...convertAdminRole(role),
    adminCount: role.adminCount,
    integrationCount: role.integrationCount,
  };
};

const convertRoles = (roles: IRole[]): Role[] => {
  return roles.map(convertRole);
};

export const converters = {
  getRoles: ({ data }: Awaited<ReturnType<typeof loaders.getRoles>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);

    return {
      items: convertRoles(data.data),
      total,
      page,
      pageSize,
      pages,
    };
  },
  getPermissions: ({
    data,
  }: Awaited<ReturnType<typeof loaders.getPermissions>>) => {
    const { total, page, pageSize, pages } = tableConverters.getTableData(data);
    const permissions = convertPermissions(data.data);

    return {
      items: {
        permissions,
        categories: groupByCategory(permissions),
      },
      total,
      page,
      pageSize,
      pages,
    };
  },
};
